import axios from "axios";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setUser } from "store/slices/userSlice";
import styled from "styled-components";
import { getTheme } from "Theme";

const theme = getTheme();

const modernFont = `'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  font-family: ${modernFont};
`;

const Popup = styled.div`
  background: #fff;
  padding: 3rem;
  border-radius: 12px;
  min-width: 400px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.25);
  font-family: ${modernFont};
`;

const Title = styled.h2`
  font-size: 2.2rem;
  margin-bottom: 1.5rem;
  font-family: ${modernFont};
`;

const PolicyText = styled.p`
  font-size: 1.3rem;
  margin-bottom: 1.5rem;
  font-family: ${modernFont};
  a {
    color: ${theme.MAIN_COLOR};
    font-weight: bold;
    font-size: 1.3rem;
    text-decoration: underline;
  }
`;

const CheckboxLabel = styled.label`
  display: block;
  margin: 1.5rem 0;
  font-size: 1.2rem;
  font-family: ${modernFont};
  input {
    transform: scale(1.3);
    margin-right: 0.7rem;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1.5rem;
  font-family: ${modernFont};
`;

const SaveButton = styled.button`
  background: ${theme.MAIN_COLOR};
  color: #fff;
  border: none;
  width: 100%;
  padding: 0.8rem 1.6rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 1.2rem;
  font-family: ${modernFont};
  transition: background 0.2s, color 0.2s, border 0.2s;
  &:hover:not(:disabled) {
    background: ${theme.MAIN_COLOR_DARK || "#0056b3"};
    color: #fff;
    border-color: ${theme.MAIN_COLOR_DARK || "#0056b3"};
  }
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
  &:hover {
    background: #eee;
    border-color: ${theme.MAIN_COLOR};
    color: ${theme.MAIN_COLOR};
  }
  transition: background 0.2s, color 0.2s, border 0.2s;
`;

const LoadingIndicator = styled.div`
  margin-top: 1.5rem;
  text-align: center;
  font-size: 1.2rem;
  color: ${theme.MAIN_COLOR};
`;

const PrivacyPolicyPopup = ({ onClose, onSave }) => {
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);

  const root = useSelector((state) => state.current.root);
  const dispatch = useDispatch();

  const handleSave = async () => {
    setLoading(true);
    try {
      let res = await axios.post("/privacy-policy", {
        acceptedPrivacyPolicy: true,
      });
      dispatch(setUser({ root: res.data }));
    } catch (error) {
      console.error("Error saving privacy policy acceptance:", error);
    } finally {
      setLoading(false);
    }
  };

  const getRootUser = async () => {
    try {
      const res = await axios.get("/root/user");
      dispatch(setUser({ root: res.data }));
    } catch (error) {
      console.error("Error fetching root user:", error);
    }
  };

  useEffect(() => {
    if (!root?._id) {
      getRootUser();
    }
  }, [root]);

  if (!root?._id || root?.acceptedPrivacyPolicy) {
    return null;
  }

  return (
    <Overlay>
      <Popup>
        <Title>Privacy Policy</Title>
        <PolicyText>
          Please read our{" "}
          <a href="/privacy" target="_blank" rel="noopener noreferrer">
            Privacy Policy
          </a>
          .
        </PolicyText>
        <CheckboxLabel>
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            disabled={loading}
          />
          I have read and agree to the Privacy Policy
        </CheckboxLabel>
        <ButtonRow>
          <SaveButton onClick={handleSave} disabled={!agreed || loading}>
            {loading ? "Saving..." : "Save"}
          </SaveButton>
        </ButtonRow>
        {loading && (
          <LoadingIndicator>Saving your acceptance...</LoadingIndicator>
        )}
      </Popup>
    </Overlay>
  );
};

export default PrivacyPolicyPopup;
